/**
 * Groups API Functions
 * Core group CRUD operations. Member/invitation actions live in groupMembersApi.ts.
 */

import { API_ENDPOINTS } from '../../../constants'
import type { Group, GroupApiResponse, GroupInviterApiResponse, GroupDashboardResponse } from '../types/groups.types'
import type { AxiosResponse } from 'axios'
import { extractErrorMessage, isNetworkError } from '../../../helpers/apiClient.helper'
import { logger } from '../../helpers/logger.helper'
import { sanitizeInput } from '../../helpers/sanitizeInput.helper'
import { GROUP_NAME_MIN_LENGTH, GROUP_NAME_MAX_LENGTH } from '../constants/groupName'
import { INVITATION_STATUS } from '../constants/groups.constants'

type ApiClientFn = <T = unknown>(endpoint: string, config?: object) => Promise<AxiosResponse<T>>

/**
 * Transform GroupApiResponse to Group with frontend-specific fields.
 * Backend returns invitationStatus, invitationType, invitationId, inviter for pending invitations.
 */
const transformGroup = (apiGroup: GroupApiResponse): Group => {
  return {
    id: apiGroup.id,
    name: apiGroup.name,
    description: apiGroup.description,
    creatorId: apiGroup.creatorId,
    memberIds: apiGroup.memberIds ?? [],
    isPrivate: apiGroup.isPrivate,
    allowMemberInvites: apiGroup.allowMemberInvites ?? true,
    createdAt: new Date(apiGroup.createdAt),
    updatedAt: new Date(apiGroup.updatedAt),
    memberCount: (apiGroup.memberIds ?? []).length,
    imageUrl: undefined,
    invitationStatus: apiGroup.invitationStatus ?? INVITATION_STATUS.ACCEPTED,
    invitationId: apiGroup.invitationId,
    invitationType: apiGroup.invitationType,
    inviter: apiGroup.inviter,
    pendingInvitees: apiGroup.pendingInvitees ?? [],
    myPosition: apiGroup.myPosition,
  }
}

/**
 * Fetch groups from backend API
 * Requires authenticated API client (Supabase JWT token)
 */
export const fetchGroups = async (
  _userId: string,
  apiClientFn: ApiClientFn
): Promise<Group[]> => {
  try {
    const groupsEndpoint = API_ENDPOINTS.groups.list
    const { data } = await apiClientFn<{ success: boolean; data?: GroupApiResponse[] }>(
      groupsEndpoint
    )

    if (data?.success && Array.isArray(data.data)) {
      return data.data.map((apiGroup) => transformGroup(apiGroup))
    }
    return []
  } catch (error) {
    const msg = extractErrorMessage(error, 'Failed to fetch groups')
    if (msg === 'No active session') return []
    if (isNetworkError(error)) {
      logger.warn('fetchGroups failed: server unreachable')
    } else {
      logger.error('fetchGroups failed', { message: msg })
    }
    return []
  }
}

/**
 * Fetch a single group by ID. Used for deep linking so the group screen can load without the full list.
 * Requires authenticated API client. Backend enforces authz (creator, member, or pending invitee).
 */
export const fetchGroupById = async (
  groupId: string,
  apiClientFn: ApiClientFn
): Promise<Group | null> => {
  try {
    const endpoint = `${API_ENDPOINTS.groups.list}/${groupId}`
    const { data } = await apiClientFn<{ success: boolean; data?: GroupApiResponse }>(endpoint)

    if (data?.success && data.data) {
      return transformGroup(data.data)
    }
    return null
  } catch (error) {
    const msg = extractErrorMessage(error, 'Failed to fetch group')
    if (msg === 'No active session') return null
    if (isNetworkError(error)) {
      logger.warn('fetchGroupById failed: server unreachable')
    } else {
      logger.error('fetchGroupById failed', { message: msg })
    }
    return null
  }
}

/**
 * Update a group's name. Only the creator can update on backend.
 * Caller should invalidate/update groups and group dashboard queries after.
 */
export const updateGroupName = async (
  groupId: string,
  name: string,
  apiClientFn: <T = unknown>(endpoint: string, config?: { method: string; data?: { name: string } }) => Promise<AxiosResponse<T>>
): Promise<GroupApiResponse | null> => {
  const trimmed = name.trim()
  if (trimmed.length < GROUP_NAME_MIN_LENGTH || trimmed.length > GROUP_NAME_MAX_LENGTH) {
    throw new Error(`Group name must be between ${GROUP_NAME_MIN_LENGTH} and ${GROUP_NAME_MAX_LENGTH} characters`)
  }
  const sanitizedName = sanitizeInput(trimmed.slice(0, GROUP_NAME_MAX_LENGTH), true)
  const endpoint = `${API_ENDPOINTS.groups.list}/${groupId}`
  const { data } = await apiClientFn<{ success: boolean; data?: GroupApiResponse; message?: string }>(endpoint, {
    method: 'PATCH',
    data: { name: sanitizedName },
  })
  if (!data?.success) {
    throw new Error(data?.message ?? 'Failed to update group name')
  }
  return data?.data ?? null
}

/**
 * Create a new group
 * Minimal helper that mirrors backend sanitization rules
 */
export const createGroup = async (
  name: string,
  apiClientFn: ApiClientFn,
  allowMemberInvites = true
): Promise<GroupApiResponse | null> => {
  // Frontend: light validation + sanitization, backend remains source of truth
  const trimmed = name.trim()
  if (!trimmed) {
    throw new Error('Group name is required')
  }

  if (trimmed.length < GROUP_NAME_MIN_LENGTH || trimmed.length > GROUP_NAME_MAX_LENGTH) {
    throw new Error(`Group name must be between ${GROUP_NAME_MIN_LENGTH} and ${GROUP_NAME_MAX_LENGTH} characters`)
  }

  const limited = trimmed.slice(0, GROUP_NAME_MAX_LENGTH)
  const sanitizedName = sanitizeInput(limited, true)

  try {
    const endpoint = API_ENDPOINTS.groups.list
    const { data } = await apiClientFn<{ success: boolean; data?: GroupApiResponse; message?: string }>(
      endpoint,
      {
        method: 'POST',
        data: { name: sanitizedName, allowMemberInvites },
      }
    )

    if (!data?.success) {
      throw new Error(data?.message || 'Failed to create group')
    }

    return data?.data ?? null
  } catch (error) {
    logger.error('createGroup failed', { message: extractErrorMessage(error, 'Failed to create group') })
    throw error
  }
}

/**
 * Member profile from GET /groups/:id/members (same shape as inviter).
 */
export type GroupMemberProfile = GroupInviterApiResponse

/**
 * Delete a group. Only creator can delete on backend.
 * Caller should refresh groups after (e.g. refreshGroups() from context).
 */
export const deleteGroup = async (
  groupId: string,
  apiClientFn: <T = unknown>(endpoint: string, config?: { method: string }) => Promise<AxiosResponse<T>>
): Promise<void> => {
  const { data } = await apiClientFn<{ success: boolean; message?: string }>(
    `${API_ENDPOINTS.groups.list}/${groupId}`,
    { method: 'DELETE' }
  )
  if (!data?.success) {
    throw new Error(data?.message ?? 'Failed to delete group')
  }
}

/**
 * Fetch aggregated group dashboard (group info + members + completed challenges).
 * Single BFF call replaces GET /:id + GET /:id/members + GET /:id/challenges.
 */
export const fetchGroupDashboard = async (
  groupId: string,
  apiClientFn: <T = unknown>(endpoint: string, config?: object) => Promise<AxiosResponse<T>>
): Promise<GroupDashboardResponse> => {
  try {
    const endpoint = `${API_ENDPOINTS.groups.list}/${groupId}/dashboard`
    const { data } = await apiClientFn<{ success: boolean; data?: GroupDashboardResponse }>(endpoint)

    if (data?.success && data.data) {
      return data.data
    }
    throw new Error('Failed to fetch group dashboard')
  } catch (error) {
    const msg = extractErrorMessage(error, 'Failed to fetch group dashboard')
    if (isNetworkError(error)) {
      logger.warn('fetchGroupDashboard failed: server unreachable')
    } else {
      logger.error('fetchGroupDashboard failed', { message: msg })
    }
    throw error
  }
}
